import { useCallback } from 'react';
import {
  streamChat,
  fetchSession,
  closeSession,
  cancelChat,
  sendMatrixMaster,
  MatrixScope,
  SelectListData,
  SelectListItem,
} from 'apis/chatbot/Chatbot';
import {
  abortChatStream,
  setChatAbortController,
  useChatbotStore,
} from '../stores/useChatbotStore';

/**
 * 챗봇 동작(세션 발급·전송·중단)만 담당한다.
 *
 * 대화 상태는 useChatbotStore에 있다 — 화면을 옮기거나 패널이 언마운트돼도 유지되어야
 * 하기 때문이다. 이 훅은 상태를 갖지 않으므로 어디서 호출해도 같은 대화를 가리킨다.
 */

/** ChatMessage 타입은 스토어가 소유한다 (기존 import 경로 유지를 위해 재수출) */
export type { ChatMessage } from '../stores/useChatbotStore';

const SESSION_TP_CHATBOT = 'CHATBOT';

const UI_ACTION_GUIDES: Record<string, string> = {
  need_matrix:
    '조회할 Matrix 정보를 입력해주세요.\n\n' +
    '💡 **이렇게 입력해보세요:**\n' +
    '- "UTM02421 v1.0 GM2 공장 사용량 알려줘"\n' +
    '- "UTM02421 1.0 GM2 조회해줘"\n\n' +
    '> UTM ID, 버전, 공장코드를 포함해서 입력해주세요.',
};

/**
 * 사용자 입력에서 matrix_scope 파싱
 * UTM ID (UTM + 숫자), 버전 (v숫자.숫자 또는 숫자.숫자), 공장코드 (영문+숫자 2~4자리)
 */
function parseMatrixScope(input: string): MatrixScope | null {
  const utmMatch = input.match(/UTM[-_]?(\d{3,})/i);
  const verMatch = input.match(/v?(\d+\.\d+)/i);
  // UTM..., v숫자.숫자 패턴을 제외한 뒤 공장코드(영문1~3 + 숫자1~2) 매칭
  const cleaned = input.replace(/UTM[-_]?\d+/gi, '').replace(/v?\d+\.\d+/gi, '');
  const plntMatch = cleaned.match(/\b([A-Z]{1,3}\d{1,2})\b/i);

  if (utmMatch && verMatch && plntMatch) {
    return {
      utmId: utmMatch[0].toUpperCase().replace(/[-_]/g, ''),
      verNo: verMatch[1],
      bildPlntCd: plntMatch[1].toUpperCase(),
    };
  }
  return null;
}

export default function useChatbot() {
  const messages = useChatbotStore((s) => s.messages);
  const isLoading = useChatbotStore((s) => s.isLoading);
  const sessionId = useChatbotStore((s) => s.sessionId);
  const pendingAction = useChatbotStore((s) => s.pendingAction);
  const setMessages = useChatbotStore((s) => s.setMessages);
  const resetMessages = useChatbotStore((s) => s.resetMessages);
  const setSessionId = useChatbotStore((s) => s.setSessionId);
  const setIsLoading = useChatbotStore((s) => s.setIsLoading);
  const setPendingAction = useChatbotStore((s) => s.setPendingAction);

  const initSession = useCallback(
    async (pageContext?: string) => {
      try {
        const id = await fetchSession(SESSION_TP_CHATBOT);
        setSessionId(id);
        console.log('[Chatbot] 세션 발급/재사용:', id);

        if (pageContext === 'ut-matrix') {
          try {
            await sendMatrixMaster(id);
            console.log('[Chatbot] matrix master 적재 완료');
          } catch (e) {
            console.error('[Chatbot] matrix master 적재 실패:', e);
          }
        }

        return id;
      } catch (e) {
        console.error('[Chatbot] 세션 발급 실패:', e);
        return null;
      }
    },
    [setSessionId]
  );

  const sendMessage = useCallback(
    async (question: string, renewSession?: boolean) => {
      const userMsg = {
        id: `user-${Date.now()}`,
        role: 'user' as const,
        content: question,
        timestamp: new Date(),
      };

      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg = {
        id: assistantId,
        role: 'assistant' as const,
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsLoading(true);

      // 세션 갱신 또는 신규 발급
      let currentSessionId = sessionId;

      // Matrix 선택 등으로 대화 흐름을 새로 시작하는 경우: 기존 세션을 닫고 재발급
      if (renewSession && currentSessionId) {
        try {
          await closeSession(currentSessionId);
          console.log('[Chatbot] 세션 종료 (갱신):', currentSessionId);
        } catch (e) {
          console.error('[Chatbot] 세션 종료 실패:', e);
        }
        currentSessionId = null;
      }

      if (!currentSessionId) {
        currentSessionId = await initSession(renewSession ? 'ut-matrix' : undefined);
        console.log('[Chatbot] sendMessage - 세션 발급:', currentSessionId);
      }

      // pendingAction이 need_matrix인 경우 사용자 입력에서 matrixScope 파싱
      let matrixScope: MatrixScope | undefined;
      console.log('[Chatbot] pendingAction:', pendingAction);
      if (pendingAction === 'need_matrix') {
        matrixScope = parseMatrixScope(question) || undefined;
        console.log('[Chatbot] matrix_scope 파싱 결과:', matrixScope);
        setPendingAction(null);
      }
      console.log('[Chatbot] 질문 전송 - sessionId:', currentSessionId);

      // 패널이 언마운트돼도 중단할 수 있어야 해서 컨트롤러는 스토어 모듈에 보관한다
      setChatAbortController(
        streamChat(
          question,
          currentSessionId,
          {
            onChunk: (chunk) => {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m))
              );
            },
            onUiAction: (action) => {
              console.log('[Chatbot] ui_action:', action);
              const guide = UI_ACTION_GUIDES[action];
              if (guide) {
                setPendingAction(action);
                setMessages((prev) =>
                  prev.map((m) => (m.id === assistantId ? { ...m, content: guide } : m))
                );
              }
            },
            onSelectList: (data: SelectListData) => {
              console.log('[Chatbot] select_list:', data);
              // items(enriched)가 있으면 사용, 없으면 matrix_list로 fallback
              const items: SelectListItem[] =
                data.items && data.items.length > 0
                  ? data.items
                  : (data.matrix_list || []).map((utmId) => ({
                      utmId,
                      utmNo: '',
                      bildPlntCd: '',
                      bildPlntCdNm: '',
                      utmNm: utmId,
                      verNo: '',
                      utmWkProgStatCdNm: '',
                    }));
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: data.assistant_message, selectItems: items }
                    : m
                )
              );
            },
            onDone: () => {
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantId) return m;
                  const content =
                    m.content.trim() === ''
                      ? '죄송합니다. 현재 해당 질문에 대한 답변을 드리기 어렵습니다.\n다른 질문을 입력해주시거나, 잠시 후 다시 시도해주세요.'
                      : m.content;
                  return { ...m, content, isStreaming: false };
                })
              );
              setIsLoading(false);
            },
            onError: () => {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? {
                        ...m,
                        content:
                          '죄송합니다. 일시적인 오류로 요청을 처리할 수 없습니다.\n잠시 후 다시 시도해주세요.',
                        isStreaming: false,
                      }
                    : m
                )
              );
              setIsLoading(false);
            },
          },
          matrixScope
        )
      );
    },
    [sessionId, pendingAction, initSession, setMessages, setIsLoading, setPendingAction]
  );

  const stopStreaming = useCallback(() => {
    abortChatStream();
    setMessages((prev) => prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)));
    setIsLoading(false);

    if (sessionId) {
      cancelChat(sessionId).catch((e) => console.error('[Chatbot] 대화 취소 실패:', e));
    }
  }, [sessionId, setMessages, setIsLoading]);

  const clearMessages = useCallback(async () => {
    abortChatStream();

    // 기존 세션 종료 후 새 세션 발급
    if (sessionId) {
      try {
        console.log('[Chatbot] 세션 종료:', sessionId);
        await closeSession(sessionId);
      } catch (e) {
        console.error('[Chatbot] 세션 종료 실패:', e);
      }
    }
    const newId = await initSession();
    console.log('[Chatbot] 새 세션 발급:', newId);

    resetMessages();
    setIsLoading(false);
  }, [sessionId, initSession, resetMessages, setIsLoading]);

  return { messages, isLoading, sessionId, sendMessage, stopStreaming, clearMessages, initSession };
}
